import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import apiMap from "../../apiMapping.json";
import "./SlokaList.css";
import FetchError from "../FetchError/FetchError";
import { createCachedGet, invalidateCachedUrl } from "../../utils/apiCache";

const cachedGet = createCachedGet(axios);

function SlokaList({ language, chapterNumber, selectedSloka, onSelectSlokaNumber }) {
  const [slokas, setSlokas] = useState([]);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const listUrl =
    apiMap.hostData + apiMap.apiData + `sloka/${language}/${chapterNumber}`;

  useEffect(() => {
    if (!chapterNumber) return;

    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setError(null);
        setLoaded(false);
        const { data: slokaResponse } = await cachedGet(listUrl, { signal: controller.signal });
        setSlokas(
          Array.isArray(slokaResponse)
            ? slokaResponse
            : [slokaResponse]
        );
        setLoaded(true);
      } catch (err) {
        if (axios.isCancel?.(err) || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
          return;
        }
        setError("Could not load verses for this chapter and language.");
        console.error(err);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [language, chapterNumber, listUrl, retryKey]);

  const handleRetry = useCallback(() => {
    invalidateCachedUrl(listUrl);
    setRetryKey((k) => k + 1);
  }, [listUrl]);

  if (error) {
    return <FetchError message={error} onRetry={handleRetry} />;
  }

  return (
    <div className="sloka-list">
      <div className="sloka-list__head">
        <h2 className="sloka-list__title">Ślokas</h2>
        <p className="sloka-list__subtitle">Chapter {chapterNumber}</p>
      </div>

      {loaded ? (
        <div className="sloka-list__chips" role="list">
          {slokas.map((sloka) => (
            <div key={sloka.slokaNumber} className="sloka-chip-wrap" role="listitem">
              <button
                type="button"
                className={
                  "sloka-chip" +
                  (selectedSloka === sloka.slokaNumber ? " sloka-chip--active" : "")
                }
                aria-current={selectedSloka === sloka.slokaNumber ? "true" : undefined}
                aria-label={`Verse ${sloka.slokaNumber}`}
                onClick={() => onSelectSlokaNumber(sloka.slokaNumber)}
              >
                {sloka.slokaNumber}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="gita-loader" aria-busy="true" aria-label="Loading verses">
          <span className="gita-loader__ring" aria-hidden />
          <span>Loading…</span>
        </div>
      )}
    </div>
  );
}

export default SlokaList;
